import { useEffect, useState } from 'react';

/**
 * Local "recently opened work orders" history.
 *
 * Powers the command palette's empty-query state and the g→l bounce-back
 * shortcut. Follows the app's client-side persistence pattern
 * (selected_account_id / selected_location_id / current_view): localStorage
 * plus a window CustomEvent for same-tab sync and the storage event for
 * cross-tab sync. History is per-browser by design; cross-device sync would
 * need a user-preferences API.
 */

export interface RecentWorkOrder {
  id: string;
  /** Human-facing number, e.g. "QL-DT-001049" */
  displayId?: string;
  /** "2022 Ford F-150 · Sarah Miller" style summary for the picker row */
  label?: string;
  /** Department the WO was in when last opened — refreshed on every visit. */
  departmentName?: string;
  accountId?: string;
  locationId?: string;
  openedAt: string;
}

const STORAGE_KEY = 'recent_work_orders';
const CHANGE_EVENT = 'recentWorkOrdersChanged';
export const MAX_RECENT_WORK_ORDERS = 10;

export function readRecentWorkOrders(): RecentWorkOrder[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e): e is RecentWorkOrder => !!e && typeof e.id === 'string');
  } catch {
    return [];
  }
}

/** Pure: newest first, de-duplicated by id, capped at MAX_RECENT_WORK_ORDERS. */
export function pushRecentWorkOrder(list: RecentWorkOrder[], entry: RecentWorkOrder): RecentWorkOrder[] {
  return [entry, ...list.filter((e) => e.id !== entry.id)].slice(0, MAX_RECENT_WORK_ORDERS);
}

function write(list: RecentWorkOrder[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {}
}

export function recordRecentWorkOrder(entry: Omit<RecentWorkOrder, 'openedAt'>): void {
  write(pushRecentWorkOrder(readRecentWorkOrders(), { ...entry, openedAt: new Date().toISOString() }));
}

/** Prune an entry, e.g. after opening it fails because the work order is gone. */
export function removeRecentWorkOrder(id: string): void {
  write(readRecentWorkOrders().filter((e) => e.id !== id));
}

/** Live view of the recents list for components; re-reads on record/remove and cross-tab changes. */
export function useRecentWorkOrders(): RecentWorkOrder[] {
  const [recents, setRecents] = useState<RecentWorkOrder[]>(readRecentWorkOrders);

  useEffect(() => {
    const sync = () => setRecents(readRecentWorkOrders());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return recents;
}
